import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Sparkle, PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";

const uuid = () => crypto.randomUUID();

export function CopilotTray({ open, onOpenChange, prefill }) {
  const [sessionId] = useState(uuid);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef();

  useEffect(() => { if (open && prefill) setInput(prefill); }, [open, prefill]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = useMutation({
    mutationFn: (question) => api.post("/copilot/portfolio", { question, session_id: sessionId }),
    onSuccess: ({ data }) => setMsgs((m) => [...m, { role: "assistant", content: data.answer }]),
    onError: (e) => toast.error(errText(e)),
  });

  const send = () => {
    const q = input.trim();
    if (!q || ask.isPending) return;
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setInput("");
    ask.mutate(q);
  };

  const SUGGESTIONS = [
    "Which clients still have open cross-year gaps?",
    "Show clients with outstanding invoices and how much they owe",
    "Draft a friendly email to Michael Chen asking for his missing K-1",
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="copilot-tray">
        <SheetHeader className="px-4 py-3 border-b border-zinc-200">
          <SheetTitle className="font-heading text-sm flex items-center gap-1.5"><Sparkle size={15} /> Portfolio Co-pilot</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {msgs.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">Query your entire portfolio in plain English — cross-client analytics, risk scans, or draft client emails with real numbers.</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="copilot-suggestion" onClick={() => { setMsgs((m) => [...m, { role: "user", content: s }]); ask.mutate(s); }}
                  className="block w-full text-left text-[11px] text-zinc-600 border border-zinc-200 rounded-md px-3 py-2 hover:border-zinc-400 transition-colors duration-150">
                  {s}
                </button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[90%] ${m.role === "user" ? "ml-auto" : ""}`}>
              <p data-testid={`copilot-msg-${m.role}`} className={`text-xs rounded-md px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.content}</p>
            </div>
          ))}
          {ask.isPending && <p className="text-[11px] text-zinc-400 flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin" /> Scanning the portfolio…</p>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-zinc-200 p-3 flex gap-2">
          <Input data-testid="copilot-input" placeholder="Ask across all clients…" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} />
          <Button data-testid="copilot-send-btn" disabled={!input.trim() || ask.isPending} onClick={send}><PaperPlaneTilt size={14} /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
